import i18nFactory from '@yandex-int/i18n';
import classNames from 'classnames';
import { Button, RadioButton, Tumbler } from 'lego-on-react';
import { intersection, memoize } from 'lodash';
import * as React from 'react';
import { ChangeEvent } from 'react';

import { NumeratedTileListItem } from '../../components/NumeratedTileListItem/NumeratedTileListItem';
import { Bem } from '../../service/Bem/Bem';
import {
    getAllSelectableChooserItemsLength,
    getTitleWeatherError,
    isRateCorrectionItemEnabledForChange,
    rateCorrectionModeTitleByModeType
} from '../../service/RateCorrectionsEditorHelpers/RateCorrectionsEditorHelpers';
import { TestBem } from '../../service/TestBem';
import { ValidationErrorData } from '../../typings/common';
import { HelpLink } from '../../typings/doc-center';
import {
    EditorDemographyRateCorrection,
    EditorDemographyRateCorrectionCondition,
    EditorDeviceRateCorrection,
    EditorDeviceRateCorrectionCondition,
    EditorRetargetingRateCorrection,
    EditorRetargetingRateCorrectionCondition,
    EditorSmartBannersRateCorrection,
    EditorSmartBannersRateCorrectionCondition,
    EditorVideoAdditionRateCorrection,
    EditorVideoAdditionRateCorrectionCondition,
    EditorWeatherRateCorrection,
    EditorWeatherRateCorrectionCondition,
    RateControlValue,
    RateCorrectionAge,
    RateCorrectionConditionChooserItem,
    RateCorrectionConditionChooserItemsGroup,
    RateCorrectionConditionId,
    RateCorrectionGender,
    RateCorrectionMode,
    RateCorrectionType,
    WeatherRateCorrectionsType
} from '../../typings/ratesCorrections';
import { Alert, AlertSize, AlertType } from '../Alert/Alert';
import { ErrorText } from '../ErrorText/ErrorText';
import {
    HoverTooltipWrapper,
    HoverTooltipWrapperTheme
} from '../HoverTooltipWrapper/HoverTooltipWrapper';
import { RateControl } from '../RateControl/RateControl';
import { RateCorrectionConditionItemsAppender } from '../RateCorrectionConditionItemsAppender/RateCorrectionConditionItemsAppender';
import { Shape2, Shape2Icon, Shape2Theme } from '../Shape2/Shape2';

import './RatesCorrectionsList.css';
import * as keyset from './RatesCorrectionsList.i18n';
import {
    onChangeDemographyConditionParams,
    RatesCorrectionsListDemographyCondition
} from './RatesCorrectionsListDemographyCondition/RatesCorrectionsListDemographyCondition';
import {
    onChangeDeviceConditionParams,
    RatesCorrectionsListDeviceCondition
} from './RatesCorrectionsListDeviceCondition/RatesCorrectionsListDeviceCondition';
import {
    onChangeRetargetingConditionParams,
    RatesCorrectionsListRetargetingCondition
} from './RatesCorrectionsListRetargetingCondition/RatesCorrectionsListRetargetingCondition';
import {
    onChangeSmartBannersConditionParams,
    RatesCorrectionsListSmartBannersCondition
} from './RatesCorrectionsListSmartBannersCondition/RatesCorrectionsListSmartBannersCondition';
import {
    onChangeVideoAdditionConditionParams,
    RatesCorrectionsListVideoAdditionCondition
} from './RatesCorrectionsListVideoAdditionCondition/RatesCorrectionsListVideoAdditionCondition';
import {
    onChangeWeatherConditionParams,
    RatesCorrectionsListWeatherCondition
} from './RatesCorrectionsListWeatherCondition/RatesCorrectionsListWeatherCondition';

const i18n = i18nFactory(keyset);

export type onChangeConditionParams =
    | onChangeDeviceConditionParams
    | onChangeDemographyConditionParams
    | onChangeRetargetingConditionParams
    | onChangeVideoAdditionConditionParams
    | onChangeSmartBannersConditionParams
    | onChangeWeatherConditionParams;

interface RatesCorrectionsListBaseProps {
    cls?: string;
    maxConditionsNumber?: number;
    maxRate?: number;
    minRate?: number;
    onAddConditions: (type: RateCorrectionType, values: Array<string>) => void;
    onDeleteCondition: (
        type: RateCorrectionType,
        conditionId: RateCorrectionConditionId
    ) => void;
    onToggleRateAvailability: (type: RateCorrectionType) => void;
    massRateControlValue?: RateControlValue;
    onChangeMassRateControl: (
        type: RateCorrectionType,
        value: RateControlValue
    ) => void;
    onChangeCondition: (params: onChangeConditionParams) => void;
    onOpenHelp: (helpTooltipId: HelpLink) => void;
    onChangeMode: (type: RateCorrectionType, value: RateCorrectionMode) => void;
    singleMassRateControlValue?: RateControlValue;
    errorsSingleMassRateControl?: ValidationErrorData[];
    onChangeSingleMassRateControl: (
        type: RateCorrectionType,
        value: RateControlValue
    ) => void;
}

type MobileRatesCorrectionsListProps = RatesCorrectionsListBaseProps &
    EditorDeviceRateCorrection & {
        chooserData: Array<RateCorrectionConditionChooserItemsGroup>;
    };

type RetargetingRatesCorrectionsListProps = RatesCorrectionsListBaseProps &
    EditorRetargetingRateCorrection & {
        chooserData: Array<RateCorrectionConditionChooserItemsGroup>;
    };

type VideoAdditionCorrectionsListProps = RatesCorrectionsListBaseProps &
    EditorVideoAdditionRateCorrection;

type SmartBannersCorrectionsListProps = RatesCorrectionsListBaseProps &
    EditorSmartBannersRateCorrection;

type DemographyRatesCorrectionsListProps = RatesCorrectionsListBaseProps &
    EditorDemographyRateCorrection;

type WeatherRatesCorrectionsListProps = RatesCorrectionsListBaseProps &
    EditorWeatherRateCorrection & {
        chooserData: Array<RateCorrectionConditionChooserItemsGroup>;
    };

export type RatesCorrectionsListProps =
    | MobileRatesCorrectionsListProps
    | RetargetingRatesCorrectionsListProps
    | DemographyRatesCorrectionsListProps
    | VideoAdditionCorrectionsListProps
    | SmartBannersCorrectionsListProps
    | WeatherRatesCorrectionsListProps;

const blockClass = 'rates-corrections-list';

export class RatesCorrectionsList extends React.PureComponent<
    RatesCorrectionsListProps
> {
    render() {
        const { type, cls } = this.props;

        return (
            <div className={classNames(blockClass, cls)}>
                <header className={Bem.cls(blockClass, 'header')}>
                    <h3 className={Bem.cls(blockClass, 'title')}>
                        {rateCorrectionTitleByType[type]}

                        <Shape2
                            cls={Bem.cls(blockClass, 'title-hint')}
                            width={12}
                            height={12}
                            icon={Shape2Icon.question}
                            theme={Shape2Theme.light}
                            hoverable={true}
                            onClick={this.onTitleInfoLinkClick}
                        />
                    </h3>

                    {this.renderInternalTabsControl()}

                    {this.getMassRateControl()}
                </header>

                {this.getItemsList()}

                <div className={Bem.cls(blockClass, 'footer')}>
                    {this.getAddCorrectionsButton()}

                    {this.getRateAvailabilityChecker()}
                </div>

                {this.getGeneralErrors()}
            </div>
        );
    }

    private renderInternalTabsControl = () => {
        const { mode } = this.props;

        if (mode === undefined) {
            return undefined;
        }

        return (
            <RadioButton
                cls={Bem.cls(blockClass, 'tabs-controls')}
                view={'default'}
                size={'n'}
                theme={'normal'}
                value={mode}
                onChange={this.onChangeMode}
            >
                <RadioButton.Radio
                    controlAttrs={{
                        title:
                            rateCorrectionModeTitleByModeType[
                                RateCorrectionMode.allDone
                            ]
                    }}
                    value={RateCorrectionMode.allDone}
                >
                    {
                        rateCorrectionModeTitleByModeType[
                            RateCorrectionMode.allDone
                        ]
                    }
                </RadioButton.Radio>
                <RadioButton.Radio
                    controlAttrs={{
                        title:
                            rateCorrectionModeTitleByModeType[
                                RateCorrectionMode.oneDone
                            ]
                    }}
                    value={RateCorrectionMode.oneDone}
                >
                    {
                        rateCorrectionModeTitleByModeType[
                            RateCorrectionMode.oneDone
                        ]
                    }
                </RadioButton.Radio>
            </RadioButton>
        );
    };

    private getMassRateControl = () => {
        const {
            items,
            massRateControlValue,
            singleMassRateControlValue,
            maxRate,
            minRate,
            isSwitchedOn,
            mode
        } = this.props;
        const itemsLength = items.length;
        let contentMassRateControl: JSX.Element | undefined;

        const isHiddenMassRateControlForOneDoneRateCorrections =
            mode === RateCorrectionMode.oneDone &&
            (itemsLength < 2 || massRateControlValue === undefined);
        const isHiddenMassRateControlForAllDoneRateCorrections =
            mode === RateCorrectionMode.allDone &&
            (itemsLength === 0 || singleMassRateControlValue === undefined);
        const isHiddenMassRateControlForRateCorrectionsWithoutMode =
            mode === undefined &&
            (itemsLength < 2 || massRateControlValue === undefined);

        if (
            isHiddenMassRateControlForOneDoneRateCorrections ||
            isHiddenMassRateControlForAllDoneRateCorrections ||
            isHiddenMassRateControlForRateCorrectionsWithoutMode
        ) {
            return null;
        }

        switch (mode) {
            case undefined:
            case RateCorrectionMode.oneDone:
                contentMassRateControl =
                    massRateControlValue !== undefined ? (
                        <>
                            {i18n('same-values-for-all-rate-control')}
                            <RateControl
                                value={massRateControlValue}
                                isDisabled={isSwitchedOn === false}
                                key={1}
                                cls={Bem.cls(blockClass, 'mass-rate-control')}
                                isCommon={true}
                                max={maxRate}
                                min={minRate}
                                onChange={this.onChangeMassRateControl}
                            />
                        </>
                    ) : (
                        undefined
                    );

                break;
            case RateCorrectionMode.allDone:
                contentMassRateControl = singleMassRateControlValue ? (
                    <>
                        {i18n('flat-mass-rate-control')}
                        <RateControl
                            value={singleMassRateControlValue}
                            isDisabled={false}
                            key={1}
                            cls={Bem.cls(blockClass, 'mass-rate-control')}
                            isCommon={false}
                            max={maxRate}
                            min={minRate}
                            onChange={this.onChangeSingleMassRateControl}
                            errors={this.props.errorsSingleMassRateControl}
                        />
                    </>
                ) : (
                    undefined
                );

                break;

            default:
                throw new Error('Неподдерживаемый тип mode для корректировки');
        }

        return contentMassRateControl ? (
            <div className={Bem.cls(blockClass, 'mass-rate')}>
                {contentMassRateControl}
            </div>
        ) : (
            undefined
        );
    };

    private getItemsList = () => {
        const { isSwitchedOn, maxRate, minRate } = this.props;

        if (this.props.items.length === 0) {
            return (
                <div className={Bem.cls(blockClass, 'no-items-message')}>
                    {i18n('no-items-message')}
                </div>
            );
        }

        let itemsContent;

        switch (this.props.type) {
            case RateCorrectionType.retargeting: {
                const { chooserData, onChangeCondition } = this.props;
                const currentRetargetingItemsIds = getRetargetingItemsIds(
                    this.props.items
                );

                itemsContent = this.props.items.map(
                    (
                        item: EditorRetargetingRateCorrectionCondition,
                        index: number
                    ) => {
                        return this.getListItem(
                            item.id,
                            index,
                            <RatesCorrectionsListRetargetingCondition
                                isDisabled={isSwitchedOn === false}
                                value={item}
                                maxRate={maxRate}
                                minRate={minRate}
                                chooserData={chooserData}
                                disabledChooserDataItems={
                                    currentRetargetingItemsIds
                                }
                                isChooserDisabled={
                                    !isRateCorrectionItemEnabledForChange(
                                        item.retargetingConditionId,
                                        chooserData,
                                        currentRetargetingItemsIds.length
                                    )
                                }
                                onChange={onChangeCondition}
                            />
                        );
                    }
                );

                break;
            }

            case RateCorrectionType.device: {
                const { chooserData, onChangeCondition } = this.props;
                const currentDeviceItemsIds = getDeviceItemsIds(
                    this.props.items
                );

                itemsContent = this.props.items.map(
                    (
                        item: EditorDeviceRateCorrectionCondition,
                        index: number
                    ) => {
                        return this.getListItem(
                            item.id,
                            index,
                            <RatesCorrectionsListDeviceCondition
                                isDisabled={isSwitchedOn === false}
                                value={item}
                                maxRate={maxRate}
                                minRate={minRate}
                                chooserData={chooserData}
                                disabledChooserDataItems={currentDeviceItemsIds}
                                isChooserDisabled={
                                    !isRateCorrectionItemEnabledForChange(
                                        item.deviceType,
                                        chooserData,
                                        currentDeviceItemsIds.length
                                    )
                                }
                                onChange={onChangeCondition}
                            />
                        );
                    }
                );

                break;
            }

            case RateCorrectionType.demography: {
                const isAllGendersDisabled = this.props.items.some(
                    item => item.age === RateCorrectionAge.all
                );
                const isAllAgesDisabled = this.props.items.some(
                    item => item.gender === RateCorrectionGender.all
                );

                itemsContent = this.props.items.map(
                    (
                        item: EditorDemographyRateCorrectionCondition,
                        index: number
                    ) => {
                        return this.getListItem(
                            item.id,
                            index,
                            <RatesCorrectionsListDemographyCondition
                                value={item}
                                index={index}
                                maxRate={maxRate}
                                minRate={minRate}
                                isDisabled={isSwitchedOn === false}
                                isAllGendersDisabled={isAllGendersDisabled}
                                isAllAgesDisabled={isAllAgesDisabled}
                                onChange={this.props.onChangeCondition}
                            />
                        );
                    }
                );

                break;
            }

            case RateCorrectionType.videoAddition: {
                itemsContent = this.props.items.map(
                    (
                        item: EditorVideoAdditionRateCorrectionCondition,
                        index: number
                    ) => {
                        return this.getListItem(
                            item.id,
                            index,
                            <RatesCorrectionsListVideoAdditionCondition
                                isDisabled={false}
                                value={item}
                                maxRate={maxRate}
                                minRate={minRate}
                                onChange={this.props.onChangeCondition}
                            />
                        );
                    }
                );

                break;
            }

            case RateCorrectionType.weather: {
                const {
                    chooserData,
                    mode,
                    minTemperatureLimit,
                    maxTemperatureLimit
                } = this.props;

                const currentWeatherActiveIds = getWeatherItemsIds(
                    this.props.items
                );

                itemsContent = this.props.items.map(
                    (
                        item: EditorWeatherRateCorrectionCondition,
                        index: number
                    ) => {
                        return this.getListItem(
                            item.id,
                            index,
                            <RatesCorrectionsListWeatherCondition
                                value={item}
                                maxRate={maxRate}
                                minRate={minRate}
                                minTemperatureLimit={minTemperatureLimit}
                                maxTemperatureLimit={maxTemperatureLimit}
                                onChange={this.props.onChangeCondition}
                                chooserData={chooserData}
                                disabledChooserDataItems={getWeatherDisabledChooserDataItems(
                                    currentWeatherActiveIds,
                                    item.weatherType,
                                    mode
                                )}
                                isHiddenRateControl={
                                    mode === RateCorrectionMode.allDone
                                }
                            />,
                            getTitleWeatherError(item, 'intersectionItem')
                        );
                    }
                );

                break;
            }

            case RateCorrectionType.smartBanners: {
                itemsContent = this.props.items.map(
                    (
                        item: EditorSmartBannersRateCorrectionCondition,
                        index: number
                    ) => {
                        return this.getListItem(
                            item.id,
                            index,
                            <RatesCorrectionsListSmartBannersCondition
                                isDisabled={false}
                                value={item}
                                maxRate={maxRate}
                                minRate={minRate}
                                onChange={this.props.onChangeCondition}
                            />
                        );
                    }
                );

                break;
            }

            default:
                throw Error('Неподдерживаемый тип корректировок ставок');
        }

        return (
            <div className={Bem.cls(blockClass, 'items')}>{itemsContent}</div>
        );
    };

    private getListItem = (
        id: RateCorrectionConditionId,
        index: number,
        content: React.ReactNode,
        tooltipText?: string
    ) => {
        const { isSwitchedOn } = this.props;

        const contentItem = (
            <NumeratedTileListItem
                itemId={id}
                key={id}
                cls={classNames(
                    Bem.cls(blockClass, 'item'),
                    TestBem.cls(blockClass, 'item', {
                        index: `${index + 1}`
                    })
                )}
                index={index + 1}
                isDisabled={isSwitchedOn === false}
                onDelete={this.onDeleteCondition}
            >
                {content}
            </NumeratedTileListItem>
        );

        return tooltipText ? (
            <HoverTooltipWrapper
                cls={classNames(
                    Bem.cls(blockClass, 'wrapper-tooltip'),
                    Bem.cls(blockClass, 'item')
                )}
                theme={'error'}
                themeWrapper={HoverTooltipWrapperTheme.clear}
                view={'classic'}
                to={['right']}
                size={'s'}
                tooltipText={tooltipText}
            >
                {contentItem}
            </HoverTooltipWrapper>
        ) : (
            contentItem
        );
    };

    private getAddCorrectionsButton = () => {
        const { isSwitchedOn, items, maxConditionsNumber, mode } = this.props;

        const isDisabled =
            isSwitchedOn === false || maxConditionsNumber === items.length;

        switch (this.props.type) {
            case RateCorrectionType.demography:
            case RateCorrectionType.videoAddition:
            case RateCorrectionType.smartBanners: {
                return this.getAppendButton(isDisabled);
            }

            case RateCorrectionType.device: {
                const chooserData = this.props.chooserData;
                const chooserDataItemsLength = getChooserDataItems(chooserData)
                    .length;
                const selectedItemsIds = getDeviceItemsIds(this.props.items);
                const disabledItems = this.getDisabledItemsInfoForAddCorrectionsButton(
                    selectedItemsIds
                );
                const isDisabledAppender =
                    isDisabled ||
                    getAllSelectableChooserItemsLength(chooserData) ===
                        selectedItemsIds.length;

                if (chooserDataItemsLength < 2) {
                    return this.getAppendButton(isDisabledAppender);
                }

                return (
                    <RateCorrectionConditionItemsAppender
                        isDisabled={isDisabledAppender}
                        checkAllControl={checkAllControlByType[this.props.type]}
                        items={chooserData}
                        disabledItems={disabledItems}
                        onChange={this.onRateConditionChooserChange}
                    />
                );
            }

            case RateCorrectionType.retargeting: {
                const chooserData = this.props.chooserData;
                const selectedRetargetingItemsIds = getRetargetingItemsIds(
                    this.props.items
                );
                const disabledItems = this.getDisabledItemsInfoForAddCorrectionsButton(
                    selectedRetargetingItemsIds
                );
                const allSelectableItemsLength = getAllSelectableChooserItemsLength(
                    chooserData
                );
                let maxSelectableItemsNumber;

                if (maxConditionsNumber !== undefined) {
                    maxSelectableItemsNumber =
                        maxConditionsNumber -
                        selectedRetargetingItemsIds.length;
                }

                return (
                    <RateCorrectionConditionItemsAppender
                        isDisabled={
                            isDisabled ||
                            allSelectableItemsLength ===
                                selectedRetargetingItemsIds.length
                        }
                        checkAllControl={checkAllControlByType[this.props.type]}
                        items={chooserData}
                        disabledItems={disabledItems}
                        hideDisabledItems={true}
                        maxSelectableItemsNumber={maxSelectableItemsNumber}
                        onChange={this.onRateConditionChooserChange}
                        searchFilter={getAppenderSearchFilterParams(
                            getAllSelectableChooserItemsLength(chooserData) -
                                disabledItems.length
                        )}
                    />
                );
            }

            case RateCorrectionType.weather: {
                const chooserData = this.props.chooserData;

                let maxSelectableItemsNumber;

                const selectedWeatherItemsIds = getWeatherItemsIds(
                    this.props.items
                );

                const disabledItems = this.getDisabledItemsInfoForAddCorrectionsButton(
                    selectedWeatherItemsIds,
                    mode
                );

                if (maxConditionsNumber !== undefined) {
                    maxSelectableItemsNumber =
                        maxConditionsNumber - selectedWeatherItemsIds.length;
                }

                return (
                    <RateCorrectionConditionItemsAppender
                        isDisabled={isDisabled}
                        checkAllControl={checkAllControlByType[this.props.type]}
                        items={chooserData}
                        disabledItems={disabledItems}
                        onChange={this.onRateConditionChooserChange}
                        maxSelectableItemsNumber={maxSelectableItemsNumber}
                    />
                );
            }

            default:
                return null;
        }
    };

    private getAppendButton = (isDisabled: boolean) => {
        return (
            <Button
                cls={TestBem.cls(blockClass, 'add-btn')}
                theme="normal"
                view="classic"
                size="s"
                onClick={this.onAddConditionButtonClick}
                disabled={isDisabled}
            >
                <Shape2
                    cls={Bem.cls(blockClass, 'add-btn-icon')}
                    width={11}
                    height={11}
                    icon={Shape2Icon.plus}
                    hoverable={true}
                />
                {i18n('add-correction')}
            </Button>
        );
    };

    private getDisabledItemsInfoForAddCorrectionsButton(
        currentValues: Array<string>,
        mode?: RateCorrectionMode
    ): Array<string> {
        if (mode === RateCorrectionMode.oneDone) {
            return [];
        }

        if (
            this.props.type === RateCorrectionType.demography ||
            this.props.type === RateCorrectionType.videoAddition ||
            this.props.type === RateCorrectionType.smartBanners
        ) {
            throw Error('Неподдерживаемый тип корректировки ставки');
        }

        const { chooserData } = this.props;

        return chooserData.reduce(
            (
                res: Array<string>,
                group: RateCorrectionConditionChooserItemsGroup
            ) => {
                const groupItemsIds = group.items.map(item => item.id);

                if (group.hasMultiChoice) {
                    const selectedItems = groupItemsIds.filter(id =>
                        currentValues.includes(id)
                    );

                    res = [...res, ...selectedItems];
                } else {
                    if (
                        intersection(currentValues, groupItemsIds).length !== 0
                    ) {
                        res = [...res, ...groupItemsIds];
                    }
                }

                return res;
            },
            []
        );
    }

    private getRateAvailabilityChecker = () => {
        const { items, isSwitchedOn } = this.props;

        // скрываем тумблер для включенных корректировок, так как дизайнеры считаю его ненужным
        // оставляем для выключенных для обратной совместимости, чтобы пользователь мог включить корректировку
        if (
            isSwitchedOn === true ||
            isSwitchedOn === undefined ||
            items.length === 0
        ) {
            return null;
        }

        return (
            <Tumbler
                cls={Bem.cls(blockClass, 'enable-checker')}
                theme="normal"
                view={'default'}
                size={'xs'}
                checked={isSwitchedOn}
                onChange={this.onToggleRateAvailability}
            />
        );
    };

    private getGeneralErrors = () => {
        const { errors } = this.props;

        if (errors.length === 0) {
            return null;
        }

        return (
            <Alert
                size={AlertSize.l}
                type={AlertType.error}
                className={classNames(
                    Bem.cls(blockClass, 'errors'),
                    TestBem.cls(blockClass, 'errors')
                )}
            >
                <ErrorText errors={errors} />
            </Alert>
        );
    };

    private onRateConditionChooserChange = (values: Array<string>) => {
        const { onAddConditions, type } = this.props;

        onAddConditions(type, values);
    };

    private onAddConditionButtonClick = () => {
        const { onAddConditions, type } = this.props;

        if (this.props.type === RateCorrectionType.device) {
            onAddConditions(
                type,
                getChooserDataItems(this.props.chooserData).map(
                    chooserItem => chooserItem.id
                )
            );
        }

        onAddConditions(type, []);
    };

    private onDeleteCondition = (conditionId: RateCorrectionConditionId) => {
        const { onDeleteCondition, type } = this.props;

        onDeleteCondition(type, conditionId);
    };

    private onToggleRateAvailability = () => {
        const { onToggleRateAvailability, type } = this.props;

        onToggleRateAvailability(type);
    };

    private onChangeMassRateControl = (value: RateControlValue) => {
        const { onChangeMassRateControl, type } = this.props;

        onChangeMassRateControl(type, value);
    };

    private onChangeSingleMassRateControl = (value: RateControlValue) => {
        this.props.onChangeSingleMassRateControl(this.props.type, value);
    };

    private onTitleInfoLinkClick = () => {
        const { onOpenHelp, type } = this.props;

        onOpenHelp(titleInfoLinkByType[type]);
    };

    private onChangeMode = (e: ChangeEvent<HTMLInputElement>) => {
        const { onChangeMode, type } = this.props;
        onChangeMode(type, e.target.value as RateCorrectionMode);
    };
}

const titleInfoLinkByType: Record<RateCorrectionType, HelpLink> = {
    [RateCorrectionType.retargeting]: HelpLink.retargetingBidsAdjustment,
    [RateCorrectionType.demography]: HelpLink.socialDemographicBidsAdjustment,
    [RateCorrectionType.device]: HelpLink.deviceBidsAdjustment,
    [RateCorrectionType.videoAddition]: HelpLink.videoExtensionBidsAdjustment,
    [RateCorrectionType.smartBanners]: HelpLink.smartAdsBidsAdjustment,
    [RateCorrectionType.weather]: HelpLink.weatherBidsAdjustment
};

const checkAllControlByType: Record<
    RateCorrectionType,
    { label: string } | undefined
> = {
    [RateCorrectionType.retargeting]: {
        label: i18n('choose-all-retargeting-conditions')
    },
    [RateCorrectionType.demography]: undefined,
    [RateCorrectionType.device]: undefined,
    [RateCorrectionType.videoAddition]: undefined,
    [RateCorrectionType.smartBanners]: undefined,
    [RateCorrectionType.weather]: undefined
};

const rateCorrectionTitleByType: Record<RateCorrectionType, string> = {
    [RateCorrectionType.retargeting]: i18n('retargeting-title'),
    [RateCorrectionType.device]: i18n('device-title'),
    [RateCorrectionType.demography]: i18n('demography-title'),
    [RateCorrectionType.videoAddition]: i18n('video-addition-title'),
    [RateCorrectionType.smartBanners]: i18n('smart-banners-title'),
    [RateCorrectionType.weather]: i18n('weather-title')
};

const getWeatherDisabledChooserDataItems = memoize(
    (
        activeItemsIds: Array<WeatherRateCorrectionsType>,
        currentId: WeatherRateCorrectionsType,
        mode?: RateCorrectionMode
    ) => {
        if (mode === RateCorrectionMode.oneDone) {
            return [currentId];
        }

        return activeItemsIds;
    },
    (...args) => JSON.stringify(args)
);

const getRetargetingItemsIds = memoize(
    (items: Array<EditorRetargetingRateCorrectionCondition>) =>
        items.map(item => item.retargetingConditionId)
);

const getDeviceItemsIds = memoize(
    (items: Array<EditorDeviceRateCorrectionCondition>) =>
        items.map(item => {
            return item.deviceType;
        })
);

const getWeatherItemsIds = memoize(
    (items: Array<EditorWeatherRateCorrectionCondition>) =>
        items.map(item => {
            return item.weatherType;
        })
);

const getAppenderSearchFilterParams = memoize((itemsLength: number) => {
    return {
        isEnabled: itemsLength > 10
    };
});

const getChooserDataItems = memoize(
    (chooserData: Array<RateCorrectionConditionChooserItemsGroup>) =>
        chooserData.reduce(
            (
                res: Array<RateCorrectionConditionChooserItem>,
                group: RateCorrectionConditionChooserItemsGroup
            ) => {
                res = res.concat(group.items);

                return res;
            },
            []
        )
);
